import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "新番速递", to: "/new-releases" },
  { label: "作品库", to: "/works" },
  { label: "常见问题", to: "/faq" },
  { label: "关于我们", to: "/about" },
  { label: "联系我们", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (q) {
      window.REACT_APP_NAVIGATE(`/works?q=${encodeURIComponent(q)}`);
    }
  };

  const onHome = location.pathname === "/";
  const transparent = onHome && !scrolled && !menuOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-[72px] max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap" aria-label="叮咚漫画网 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-book-open-fill text-lg" />
          </span>
          <span
            className={`font-heading text-xl md:text-2xl font-bold tracking-wide ${
              transparent ? "text-background-50" : "text-primary-700"
            }`}
          >
            叮咚漫画网
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
          {navLinks.map((link) => {
            const active = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`relative whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? transparent
                      ? "text-secondary-300"
                      : "text-primary-700"
                    : transparent
                    ? "text-background-50/90 hover:text-background-50"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {link.label}
                {active && (
                  <span className="absolute left-1/2 -bottom-0.5 h-0.5 w-6 -translate-x-1/2 rounded-full bg-primary-500" />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <form onSubmit={handleSearch} className="flex items-center gap-0" role="search">
            <div
              className={`flex items-center rounded-full border px-3 ${
                transparent ? "border-background-50/40 bg-background-50/10" : "border-background-300 bg-background-100"
              }`}
            >
              <i className={`ri-search-line text-sm ${transparent ? "text-background-50/80" : "text-foreground-500"}`} />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画"
                aria-label="搜索漫画"
                className={`w-28 bg-transparent py-2 pl-2 text-sm outline-none placeholder:text-foreground-500 ${
                  transparent ? "text-background-50 placeholder:text-background-50/70" : "text-foreground-900"
                }`}
              />
            </div>
          </form>
          <Link
            to="/new-releases"
            className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            立即追番
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-lg md:hidden"
          aria-label="切换菜单"
          aria-expanded={menuOpen}
        >
          <i
            className={`text-2xl ${transparent ? "text-background-50" : "text-foreground-900"} ${
              menuOpen ? "ri-close-line" : "ri-menu-line"
            }`}
          />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-lg px-4 py-3 text-sm font-medium ${
                  location.pathname === link.to
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-800 hover:bg-background-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <form onSubmit={handleSearch} className="mt-3 flex gap-2">
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画"
              aria-label="搜索漫画"
              className="flex-1 rounded-lg border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-lg bg-primary-500 px-5 text-sm font-semibold text-background-50"
            >
              搜索
            </button>
          </form>
        </div>
      )}
    </header>
  );
}